import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Default neutral theme (will be replaced by client theme)
const DEFAULT_THEME = {
  primary_color: "#3B82F6",
  secondary_color: "#6366F1",
  background_primary: "#0F172A",
  background_secondary: "#1E293B",
  text_primary: "#F8FAFC",
  text_secondary: "#CBD5E1",
  border_color: "#475569"
};

export default function B2BUserLogin() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  
  // Check if user just registered
  const justRegistered = location.state?.justRegistered;
  const registeredEmail = location.state?.email;
  
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  
  const [formData, setFormData] = useState({
    email: registeredEmail || '',
    password: ''
  });

  useEffect(() => {
    fetchClientInfo();
  }, [clientId]);

  // Apply CSS variables for B2B theme when theme state changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS variables for global styling (including toasts)
    root.style.setProperty('--theme-primary', theme.primary_color);
    root.style.setProperty('--theme-accent', theme.secondary_color || theme.primary_color);
    root.style.setProperty('--theme-card', theme.background_secondary);
    root.style.setProperty('--theme-text', theme.text_primary);
    root.style.setProperty('--theme-border', theme.border_color);
    
    // Calculate button text color based on primary color luminance
    const hex = theme.primary_color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    root.style.setProperty('--theme-button-text', luminance > 0.5 ? '#1a1a2e' : '#ffffff');
    
    // Cleanup on unmount - reset to defaults
    return () => {
      root.style.removeProperty('--theme-primary');
      root.style.removeProperty('--theme-accent');
      root.style.removeProperty('--theme-card');
      root.style.removeProperty('--theme-text');
      root.style.removeProperty('--theme-border');
      root.style.removeProperty('--theme-button-text');
    };
  }, [theme]);

  const fetchClientInfo = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/b2b/client-info/${clientId}`);
      setClientData(res.data);
      
      // Apply client theme
      if (res.data.theme) {
        setTheme({ ...DEFAULT_THEME, ...res.data.theme });
      } else if (res.data.primary_color) {
        setTheme(prev => ({
          ...prev,
          primary_color: res.data.primary_color,
          secondary_color: res.data.secondary_color || res.data.primary_color
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Client portal not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) {
      console.log('[B2B Login] Already submitting, ignoring duplicate');
      return;
    }
    
    if (!formData.email || !formData.password) {
      toast.error('Please enter your email and password');
      return;
    }
    
    try {
      setSubmitting(true);
      console.log('[B2B Login] Attempting login for:', formData.email, 'client:', clientId);
      
      const res = await axios.post(`${API}/b2b/user-login`, {
        client_id: clientId,
        email: formData.email,
        password: formData.password
      });
      
      console.log('[B2B Login] Response:', res.data);
      
      if (res.data.success) {
        // Use custom styled toast with client's theme color
        toast.success('Welcome back!', {
          style: {
            background: theme.primary_color,
            color: '#ffffff',
            border: 'none'
          }
        });
        
        // Small delay to ensure cookie is set before checking auth
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // IMPORTANT: Set the user data in localStorage so checkAuth recognizes the session
        // This avoids the race condition with httpOnly cookies being invisible to JS
        if (res.data.user) {
          localStorage.setItem('uncloned_user', JSON.stringify(res.data.user));
          console.log('[B2B Login] User data cached in localStorage');
        }
        
        // Refresh the auth context to pick up the new session
        console.log('[B2B Login] Calling checkAuth...');
        try {
          await checkAuth();
          console.log('[B2B Login] checkAuth complete');
        } catch (authErr) {
          console.error('[B2B Login] checkAuth error:', authErr);
          // Continue with navigation even if checkAuth fails - cookie should still work
        }
        
        // Redirect to B2B portal-specific dashboard URL
        console.log('[B2B Login] Navigating to dashboard...');
        navigate(`/portal/${clientId}/dashboard`);
      } else {
        console.log('[B2B Login] Login response not successful:', res.data);
        toast.error('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('[B2B Login] Full error:', err);
      console.error('[B2B Login] Error response:', err.response?.data);
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.background_primary }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary_color }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: theme.background_primary }}>
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: theme.text_primary }}>Portal Not Found</h1>
          <p style={{ color: theme.text_secondary }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: theme.background_primary // SOLID color, no gradient for B2B
      }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ 
          background: theme.background_secondary,
          border: `1px solid ${theme.border_color}`
        }}
      >
        {/* Client Logo */}
        <div className="text-center mb-8">
          {clientData?.logo_url ? (
            <img 
              src={clientData.logo_url} 
              alt={clientData.name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <h1 
              className="text-3xl font-bold mb-2"
              style={{ color: theme.primary_color }}
            >
              {clientData?.name || 'Member Portal'}
            </h1>
          )}
          <p style={{ color: theme.text_secondary }}>
            Sign in to access your {clientData?.app_name || 'journaling'} account
          </p>
        </div>

        {/* Success message if just registered */}
        {justRegistered && (
          <div 
            className="flex items-center gap-2 p-3 rounded-lg mb-4"
            style={{ 
              background: `${theme.primary_color}20`,
              border: `1px solid ${theme.primary_color}40`
            }}
          >
            <CheckCircle className="w-5 h-5" style={{ color: theme.primary_color }} />
            <p className="text-sm" style={{ color: theme.text_primary }}>
              Account created! Sign in with your new password.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text_secondary }}
            >
              Email Address
            </label>
            <div className="relative">
              <Mail 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: theme.text_secondary }}
              />
              <Input
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="pl-10 h-12 focus-visible:ring-0 focus:ring-2 focus:outline-none"
                style={{
                  background: theme.background_primary,
                  border: `1px solid ${theme.border_color}`,
                  color: theme.text_primary,
                  '--tw-ring-color': theme.primary_color
                }}
                onFocus={(e) => e.target.style.borderColor = theme.primary_color}
                onBlur={(e) => e.target.style.borderColor = theme.border_color}
                data-testid="b2b-login-email"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text_secondary }}
            >
              Password
            </label>
            <div className="relative">
              <Lock 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: theme.text_secondary }}
              />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="pl-10 pr-10 h-12 focus-visible:ring-0 focus:ring-2 focus:outline-none"
                style={{
                  background: theme.background_primary,
                  border: `1px solid ${theme.border_color}`,
                  color: theme.text_primary,
                  '--tw-ring-color': theme.primary_color
                }}
                onFocus={(e) => e.target.style.borderColor = theme.primary_color}
                onBlur={(e) => e.target.style.borderColor = theme.border_color}
                data-testid="b2b-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: theme.text_secondary }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-white font-semibold rounded-lg transition-all duration-200"
            style={{
              background: theme.primary_color, // SOLID color for B2B
              opacity: submitting ? 0.7 : 1
            }}
            data-testid="b2b-login-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center space-y-3">
          <p className="text-sm" style={{ color: theme.text_secondary }}>
            <button
              type="button"
              onClick={() => navigate(`/portal/${clientId}/forgot-password`)}
              className="hover:underline"
              style={{ color: theme.primary_color }}
            >
              Forgot your password?
            </button>
          </p>
          <p className="text-sm" style={{ color: theme.primary_color }}>
            Don't have an account?{' '}
            <span className="hover:underline cursor-default">
              Contact your administrator for an invite
            </span>
          </p>
        </div>

        {/* Powered By */}
        <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: theme.border_color }}>
          <p className="text-xs" style={{ color: theme.text_secondary, opacity: 0.6 }}>
            Powered by UnCloned® Journal
          </p>
        </div>
      </div>
    </div>
  );
}
